<template>
  <el-col :span="24" id="accounts">
    <el-row type="flex" justify="start" align="middle" id="accounts-header">
      <HeaderInfoText :colspan="9" title="ACCOUNTS" :meta="meta" :selection="selection" />
      <el-col :span="6">
        <el-row type="flex" justify="center" v-if="meta.count > 20">
          <el-pagination
            layout="prev, pager, next, jumper"
            :total="meta.count"
            :page-size="20"
            :current-page.sync="currentPage">
          </el-pagination>
        </el-row>
      </el-col>
      <el-col :span="9" class="h-padded right-aligned-col">
        <el-select
          :disabled="!(selection && selection.length)"
          v-model="batchUpdateSelection"
          placeholder="With selected..."
          style="padding: 0 6px;"
          @change="handleBatchUpdateSelection">
          <el-option v-for="option in batchUpdateOptions" :key="option.id" :label="option.text" :value="option.id" :disabled="option.disabled" />
        </el-select>
        <el-button plain @click="toggleFilters">
          {{ showFilters ? "Hide options" : "Show options" }}
        </el-button>
        <el-button :disabled="!hasCreatePermission" type="info" @click="printAccountList">Print</el-button>
        <el-button :disabled="!hasCreatePermission" type="primary" icon="plus" @click="createNewModel" />
      </el-col>
    </el-row>
    <el-row type="flex" justify="center" align="middle" style="padding: 0 12px;" v-loading.body="!dataLoaded">
      <Filters
        localSearch="accountLocalSearch"
        clearLocalSearch="clearAccountLocalSearch"
        :filters="filters"
        :filtersVisible="showFilters"
        :filterSize="4"
        :searchForDeps="false"
        :autoApply="false"
        @applied="filtersHaveBeenApplied">
        <template v-for="(f, i) in filters" slot-scope="props" :slot="`${f.type}${i}`">
          <component :key="i" :is="f.type" :properties="f.props" />
        </template>
      </Filters>
    </el-row>
    <el-row type="flex" justify="end" align="middle" v-if="!!dataLoaded">
      <el-col :span="24">
        <el-table
          v-if="meta.count && !changingPage"
          id="accounts-table"
          :ref="`${endpoint}Table`"
          :data="pageData"
          border
          style="width: 100%; z-index: 0;"
          :maxHeight="tableHeight"
          @select="handleTableSelectionChange"
          @select-all="handleTableSelectionChange"
          :row-class-name="tableRowClassName">
          <el-table-column type="selection" width="55" align="center" />
          <el-table-column prop="code" label="Code" align="center" width="120">
            <template slot-scope="scope">
              <el-row type="flex" justify="center">
                <a class="link-mock" @click="editExistingModel(scope.row.id)">
                  {{ scope.row.code }}
                </a>
              </el-row>
            </template>
          </el-table-column>
          <el-table-column prop="name" label="Name" align="left" fit>
            <template slot-scope="scope">
              <el-row type="flex" justify="start">
                <a class="link-mock" @click="editExistingModel(scope.row.id)">
                  {{ scope.row.name }}
                </a>
              </el-row>
            </template>
          </el-table-column>
          <el-table-column prop="type" label="Type" align="left" width="155" />
          <el-table-column prop="parent" label="Parent" align="left" width="200">
            <template slot-scope="scope">
              {{ scope.row.parent && getAccountById(scope.row.parent) ? getAccountById(scope.row.parent).name : '' }}
            </template>
          </el-table-column>
          <el-table-column prop="postable" label="Postable" align="center" width="100">
            <template slot-scope="scope">
              <el-tag :type="scope.row.postable ? 'success' : 'danger'" close-transition>
                {{ scope.row.postable ? 'Yes' : 'No' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="is_summarized" label="Summarized" align="center" width="125">
            <template slot-scope="scope">
              <el-checkbox v-model="scope.row.is_summarized" @change="handleIsSummarizedChange(scope.row)" />
            </template>
          </el-table-column>
        </el-table>
      </el-col>
    </el-row>
    <el-dialog
      :title="editMode.active ? hasCreatePermission ? `Edit Account` : `View Account` : 'Create New Account'"
      size="small"
      :visible.sync="dialogFormVisible"
      :close-on-press-escape="false"
      :close-on-click-modal="false"
      :show-close="false">
      <NewEntityForm
        :modalOpen="dialogFormVisible"
        :fields="fields"
        :form.sync="form"
        :idPrefix="'a-'"
        :errMsg="errMsg"
        ref="form"
        @formChanged="handleFormChange">
      </NewEntityForm>
      <span slot="footer" class="dialog-footer">
        <el-button :disabled="saving || !!updating" @click="closeFormModal">Cancel</el-button>
        <el-button :disabled="!hasCreatePermission" :loading="saving || !!updating" type="primary" @click="submitForm">Submit</el-button>
      </span>
    </el-dialog>
  </el-col>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { payload } from '@/components/RouteComponents/Accounts/actionPayload'
  import store from '@/store/store'
  import NewEntityForm from '@/components/NewEntityForm.vue'
  import Filters from '@/components/Filters.vue'
  import KeywordFilter from '@/components/FilterComponents/KeywordFilter'
  import SelectFilter from '@/components/FilterComponents/SelectFilter'
  import HeaderInfoText from '@/components/HeaderInfoText'
  import {
    batchUpdate,
    titleCase,
    upperCase,
    createNewModel
  } from '@/components/RouteComponents/commonFunctions'
  import { systemViewMixins } from '@/Mixins/SystemViewMixins'
  import { DataLoader } from '@/utils/helpers/DataLoader/DataLoader'

  export default {
    components: {
      NewEntityForm,
      Filters,
      KeywordFilter,
      SelectFilter,
      HeaderInfoText
    },
    data () {
      return {
        filters: [
          {
            type: 'keywordFilter',
            props: {}
          },
          {
            type: 'selectFilter',
            props: {
              filterName: 'type',
              clearable: true,
              opts: [
                {label: 'Asset', value: 'ASSET'},
                {label: 'Expense: Other', value: 'EXPENSE'},
                {label: 'Expense: ATL', value: 'EXPENSE_ATL'},
                {label: 'Expense: BTL', value: 'EXPENSE_BTL'},
                {label: 'Expense: Post', value: 'EXPENSE_POST'},
                {label: 'Liability', value: 'LIABILITY'},
                {label: 'Income', value: 'INCOME'},
                {label: 'Capital', value: 'CAPITAL'},
                {label: 'Header', value: 'HEADER'}
              ],
              multi: false,
              placeholder: 'Account type'
            }
          }
        ],
        tableHeight: 1000,
        showFilters: true,
        batchUpdateOptions: [
          { id: 1, text: 'Set as postable', type: 'edit', disabled: false },
          { id: 2, text: 'Set as not postable', type: 'edit', disabled: false },
          { id: 3, text: 'Set as summarized', type: 'edit', disabled: false },
          { id: 4, text: 'Set as not summarized', type: 'edit', disabled: false },
          { id: 5, text: 'Convert names to Title Case', type: 'edit', disabled: false },
          { id: 6, text: 'Convert names to UPPER Case', type: 'edit', disabled: false }
        ],
        batchUpdateSelection: null,
        selection: [],
        loadingOptions: false,
        options: [],
        searchTimeout: null,
        changingPage: false,
        dialogFormVisible: false,
        editMode: { active: false, modelId: null },
        errMsg: null,
        removedParentChoice: null
      }
    },
    mixins: [ systemViewMixins ],
    methods: {
      /**
       * Handles showing & hiding filters
       */
      toggleFilters () {
        !this.showFilters ? this.showFilters = true : this.showFilters = false
      },
      /**
       * Handles what happens when the user clicks on one of the "With selected..." dropdown options
       */
      handleBatchUpdateSelection () {
        switch (this.batchUpdateSelection) {
          case 1:
            batchUpdate(this, 'update', payload, { postable: true })
            break
          case 2:
            batchUpdate(this, 'update', payload, { postable: false })
            break
          case 3:
            batchUpdate(this, 'update', payload, { is_summarized: true })
            break
          case 4:
            batchUpdate(this, 'update', payload, { is_summarized: false })
            break
          case 5:
            titleCase(this, payload)
            break
          case 6:
            upperCase(this, payload)
            break
        }
        this.batchUpdateSelection = null
      },
      /**
       * Resets editMode data, resets form to initial state, and displays the
       * modal to create a new model
       */
      createNewModel () {
        createNewModel(this)
      },
      printAccountList () {
        this.$router.push({ name: 'AccountListCustom' })
      },
      updateBatchUpdateOptions () {
        this.batchUpdateOptions.forEach(option => {
          if (option.type === 'edit') {
            option.disabled = !this.hasCreatePermission
          }
        })
      },
      /**
       * Resets the page to 1 once filters have been applied.
       */
      filtersHaveBeenApplied () {
        this.currentPage = 1
      },
      /**
       * Handle changes to the account create/edit form
       * @param field   The field that was changed
       */
      handleFormChange (field) {
        if (field === 'type' && this.form.type) {
          this.form.postable = this.form.type.value !== 'HEADER'
        }
        if (field === 'code' && this.form.parent && this.form.parent.value) {
          const correspondingAcct = this.data.find(account => account.id === this.form.parent.value)
          let choiceCode = correspondingAcct && correspondingAcct.id ? correspondingAcct.code : null
          if (choiceCode === this.form.code) {
            this.form.parent = { label: '', value: '' }
            this.errMsg = 'Selected parent account cannot be the same as this account.'
          } else {
            this.errMsg = null
          }
        } else if (field === 'code' && this.form.parent && !this.form.parent.value) {
          this.errMsg = null
        }
        this.trackFormChange()
      },
      /**
       * Handle is_summarized checkbox change and send PATCH request to API
       * @param row   The account row object
       */
      async handleIsSummarizedChange (row) {
        let newPayload = Object.assign({}, this.payload, { id: row.id, data: { is_summarized: row.is_summarized } })
        const resp = await this.$store.dispatch('update', newPayload)
        if (resp && resp.id) {
          this.$message.success('Account updated successfully')
        } else {
          this.$message.error('Validation failed. Please check the account data.')
          // Revert the change
          row.is_summarized = !row.is_summarized
        }
      }
    },
    computed: {
      ...mapGetters({
        getAccountById: 'getAccountById',
        dataLoaded: 'getInitDataLoadedStatus',
        activeFilters: 'getFilters',
        checkIfUserHasRequiredPermissions: 'checkIfUserHasRequiredPermissions'
      }),
      data () {
        return this.dataLoaded ? this.sortData(this.$store.getters.getAllAccounts) : this.$store.getters.getAllAccounts
      },
      pageData () {
        return this.meetsFilterCriteria(this.data).slice((this.currentPage - 1) * 20, this.currentPage * 20)
      },
      meta () {
        return { count: this.meetsFilterCriteria(this.data).length, shown: this.pageData.length }
      },
      hasCreatePermission () {
        return this.checkIfUserHasRequiredPermissions(['system.view_account', 'system.add_account'])
      },
      fields () {
        const fields = this.$store.getters.getResourceFieldsByEndpoint(this.endpoint)
        if (fields && typeof fields === 'object') {
          return Object.values(fields)
            .filter(field => field.name !== 'meta_data')
            .map(field => {
              if (!this.hasCreatePermission) {
                field.disabled = true
              }
              if (field.name === 'postable') {
                field.defaultVal = true
              }
              if (!(field.name === 'parent' && this.form)) { // No need to continue if we're not dealing with 'parent' field
                return field
              }
              if (this.form.type && this.form.type.value) {
                field.required = this.form.type.value !== 'HEADER'
              }
              if (!(this.form.code && field.choices && Array.isArray(field.choices) && field.choices.length)) {
                return field
              }
              const indexOfMatchingParentChoice = field.choices.findIndex(choice => { // Find the matching parent account choice, if any
                const splitDisplayName = choice.display_name.split(' - ')
                let choiceCode = splitDisplayName && splitDisplayName.length ? splitDisplayName[0] : null
                return choiceCode === this.form.code
              })
              if (indexOfMatchingParentChoice > -1) { // If one is found, remove choice from list of parent code choices
                this.removedParentChoice = {
                  choice: field.choices.splice(indexOfMatchingParentChoice, 1)[0],
                  index: indexOfMatchingParentChoice
                }
              } else if (this.removedParentChoice && this.form.code !== this.removedParentChoice.choice.display_name.split(' - ')[0]) {  // Otherwise, add it back to the list
                field.choices.splice(this.removedParentChoice.index, 0, this.removedParentChoice.choice)
                this.removedParentChoice = null
              }
              if (!this.dialogFormVisible && this.removedParentChoice) { // If modal is closed, and a choice was removed, then add it back to the list
                field.choices.splice(this.removedParentChoice.index, 0, this.removedParentChoice.choice)
                this.removedParentChoice = null
              }
              return field
            })
        }
        return []
      }
    },
    watch: {
      pageData () {
        if (this.restoreTableSelection && typeof this.restoreTableSelection === 'function') {
          this.restoreTableSelection()
        }
      }
    },
    /**
     * Pre-route enter logic.
     * @param to
     * @param from
     * @param next
     */
    async beforeRouteEnter (to, from, next) {
      let initPromises = []
      payload.endpoint = 'accounts'
      const fieldsAlreadyExist = !typeof store.getters.getResourceFieldsByEndpoint(payload.endpoint) === 'undefined'
      if (!fieldsAlreadyExist) {
        initPromises.push(store.dispatch('fetchOptions', payload))
      }
      try {
        await Promise.all(initPromises)
        let dataLoader = new DataLoader(store, [ payload ], true, 200)
        await dataLoader.loadData()
      } catch (e) {
        console.log(e)
      } finally {
        next()
      }
    },
    created () {
      this.endpoint = payload.endpoint
      this.moduleName = payload.moduleName
      this.payload = payload
      this.generateForm()
      this.updateBatchUpdateOptions()
    },
    async mounted () {
      this.$nextTick = () => {
        const tableHeight = () => {
          const parentHeight = document.getElementById('accounts').offsetHeight
          const headerHeight = document.getElementById('accounts-header').offsetHeight
          return parentHeight - headerHeight
        }
        this.tableHeight = tableHeight()
      }
    }
  }
</script>

<style scoped>
  /*Input form table style properties*/
  table {
    border-spacing: 0;
    border: 1px solid #bfcbd9;
    background-color: #efefef;
  }
  /*Table style properties*/
  #accounts-header {
    border-bottom: 1px solid lightgray;
  }
  .h-padded {
    padding: 0 12px;
  }
  .right-aligned-col {
    text-align: right;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
</style>



// WEBPACK FOOTER //
// src/components/RouteComponents/Accounts/Accounts.vue